/* Attendance stats & trends dashboard (Admin/Director/Educator).
 * Path: /attendance-stats
 * - Summary counters (present/absent/retard/excuse + %)
 * - Recharts line chart of daily trend over the selected window
 * - Bar chart of absence rate per class
 * - Table of students with >20% absence rate
 */
import { useEffect, useMemo, useState } from 'react';
import { studentAttendanceApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { ChartLine, CheckCircle, XCircle, Clock, ShieldCheck, Warning, ArrowClockwise, FilePdf, DownloadSimple } from '@phosphor-icons/react';

const WINDOWS = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
];

const StatCard = ({ label, value, subLabel, colorClass, Icon, testId }) => (
  <Card className={`border shadow-none ${colorClass.border}`} data-testid={testId}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide font-semibold text-[#64748B]">{label}</p>
        <p className={`font-outfit text-2xl font-bold mt-0.5 ${colorClass.text}`}>{value}</p>
        <p className="text-xs text-[#64748B] mt-0.5">{subLabel}</p>
      </div>
      <div className={`p-2 rounded-lg ${colorClass.bg}`}>
        <Icon size={22} weight="duotone" className={colorClass.iconText} />
      </div>
    </CardContent>
  </Card>
);

const AttendanceStats = () => {
  const [win, setWin] = useState(30);
  const [classFilter, setClassFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await studentAttendanceApi.stats(win, classFilter || undefined);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [win, classFilter]);

  const summary = data?.summary || { present: 0, absent: 0, retard: 0, excuse: 0, total: 0 };
  const trend = data?.trend || [];
  const highAbsence = data?.high_absence || [];
  const byClass = (data?.by_class || []).slice(0, 12);

  const classOptions = useMemo(() => {
    const set = new Set(byClass.map((c) => c.class_name).filter(Boolean));
    return Array.from(set).sort();
  }, [byClass]);

  return (
    <div className="p-6 space-y-4" data-testid="attendance-stats-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
            <ChartLine size={26} className="text-[#0052FF]" weight="duotone" />
            Statistiques de présence
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Tendances, taux d&apos;absence et élèves à risque sur {win} jours.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="stats-refresh-btn">
          <ArrowClockwise size={16} className="mr-2" />
          Rafraîchir
        </Button>
        <Button
          onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL || '';
            window.open(`${base}/api/student-attendance/monthly-report.pdf`, '_blank');
          }}
          className="bg-red-600 hover:bg-red-700"
          data-testid="stats-download-pdf-btn"
        >
          <FilePdf size={16} className="mr-2" />
          Rapport mensuel (PDF)
        </Button>
        <Button
          onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL || '';
            window.open(`${base}/api/student-attendance/quarterly-report.pdf`, '_blank');
          }}
          variant="outline"
          className="border-red-600 text-red-600 hover:bg-red-50"
          data-testid="stats-download-quarterly-pdf-btn"
        >
          <FilePdf size={16} className="mr-2" />
          Rapport trimestriel (PDF)
        </Button>
        <Button
          onClick={() => {
            const base = process.env.REACT_APP_BACKEND_URL || '';
            const params = new URLSearchParams({ window: String(win) });
            if (classFilter) params.set('class_name', classFilter);
            window.open(`${base}/api/student-attendance/export.csv?${params.toString()}`, '_blank');
          }}
          variant="outline"
          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          data-testid="stats-export-csv-btn"
        >
          <DownloadSimple size={16} className="mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Window + class filter */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4 flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs">Fenêtre</Label>
            <div className="mt-1 flex gap-1" data-testid="stats-window-picker">
              {WINDOWS.map((w) => (
                <button
                  key={w.value}
                  onClick={() => setWin(w.value)}
                  className={`px-3 h-9 rounded-md text-sm font-medium border transition ${
                    win === w.value
                      ? 'bg-[#0052FF] text-white border-[#0052FF]'
                      : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-slate-400'
                  }`}
                  data-testid={`stats-window-${w.value}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Classe</Label>
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="mt-1 w-52 h-9 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
              data-testid="stats-class-filter"
            >
              <option value="">Toutes les classes</option>
              {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="ml-auto text-xs text-[#64748B] self-center">
            {data?.since && <>Depuis le <span className="font-medium">{data.since}</span></>}
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="stats-summary-grid">
        <StatCard
          testId="stat-present"
          label="Présents"
          value={summary.present}
          subLabel={`${summary.present_pct ?? 0}% des relevés`}
          colorClass={{ border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', iconText: 'text-emerald-600' }}
          Icon={CheckCircle}
        />
        <StatCard
          testId="stat-absent"
          label="Absents"
          value={summary.absent}
          subLabel={`${summary.absent_pct ?? 0}% des relevés`}
          colorClass={{ border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700', iconText: 'text-red-600' }}
          Icon={XCircle}
        />
        <StatCard
          testId="stat-retard"
          label="Retards"
          value={summary.retard}
          subLabel={`${summary.retard_pct ?? 0}% des relevés`}
          colorClass={{ border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-700', iconText: 'text-amber-600' }}
          Icon={Clock}
        />
        <StatCard
          testId="stat-excuse"
          label="Excusés"
          value={summary.excuse}
          subLabel={`${summary.excuse_pct ?? 0}% des relevés`}
          colorClass={{ border: 'border-slate-300', bg: 'bg-slate-100', text: 'text-slate-800', iconText: 'text-slate-700' }}
          Icon={ShieldCheck}
        />
      </div>

      {/* Trend chart */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base">Tendance journalière</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-64 flex items-center justify-center text-[#94A3B8]">Chargement…</div>
          ) : (
            <div style={{ width: '100%', height: 300 }} data-testid="stats-trend-chart">
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => (d ? d.slice(5) : '')}
                    tick={{ fontSize: 11, fill: '#64748B' }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, border: '1px solid #E2E8F0', borderRadius: 6 }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="present" stroke="#10B981" strokeWidth={2} dot={false} name="Présents" />
                  <Line type="monotone" dataKey="absent" stroke="#EF4444" strokeWidth={2} dot={false} name="Absents" />
                  <Line type="monotone" dataKey="retard" stroke="#F59E0B" strokeWidth={2} dot={false} name="Retards" />
                  <Line type="monotone" dataKey="excuse" stroke="#0F172A" strokeWidth={2} dot={false} name="Excusés" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-class + high absence side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base">Taux d&apos;absence par classe</CardTitle>
          </CardHeader>
          <CardContent>
            {byClass.length === 0 ? (
              <p className="text-sm text-[#94A3B8] p-4 text-center">Aucune donnée</p>
            ) : (
              <div style={{ width: '100%', height: 280 }} data-testid="stats-by-class-chart">
                <ResponsiveContainer>
                  <BarChart data={byClass} margin={{ top: 5, right: 15, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="class_name" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} unit="%" />
                    <Tooltip
                      contentStyle={{ fontSize: 12, border: '1px solid #E2E8F0', borderRadius: 6 }}
                      formatter={(v) => `${v}%`}
                    />
                    <Bar dataKey="absent_pct" fill="#EF4444" name="% Absent" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-red-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Warning size={18} className="text-red-600" weight="fill" />
              Élèves à surveiller (&gt;20% d&apos;absence)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {highAbsence.length === 0 ? (
              <div className="p-6 text-center" data-testid="stats-no-high-absence">
                <CheckCircle size={28} className="mx-auto text-emerald-500 mb-2" weight="duotone" />
                <p className="text-sm text-[#64748B]">Aucun élève au-dessus du seuil sur cette période.</p>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Élève</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead className="text-right">Abs. / Total</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {highAbsence.map((s) => (
                      <TableRow key={s.student_id} data-testid={`stats-high-row-${s.student_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {s.photo_base64 ? (
                              <img src={s.photo_base64} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[10px] font-bold text-[#64748B]">
                                {(s.student_name || '').split(' ').map(p => p[0]).join('').slice(0, 2)}
                              </div>
                            )}
                            <span className="text-sm font-medium">{s.student_name}</span>
                          </div>
                        </TableCell>
                        <TableCell><span className="font-mono text-xs">{s.class_name}</span></TableCell>
                        <TableCell className="text-right text-xs font-mono">{s.absent_days} / {s.total_days}</TableCell>
                        <TableCell className="text-right">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                            s.absent_pct >= 40 ? 'bg-red-600 text-white' :
                            s.absent_pct >= 30 ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>{s.absent_pct}%</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AttendanceStats;
